import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tractor, Plus, Edit, Trash2, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import MaquinariaLayout from '@/components/MaquinariaLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Maquinaria({ user, onLogout }) {
  const navigate = useNavigate();
  const [vehiculos, setVehiculos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVehiculo, setEditingVehiculo] = useState(null);
  const [vehiculoForm, setVehiculoForm] = useState({
    placa: '',
    marca: '',
    modelo: '',
    ano: new Date().getFullYear(),
    tipo_vehiculo: 'Pickup',
    color: '',
    numero_vin: '',
    numero_motor: '',
    serie: '',
    capacidad: '',
    estado_actual: 'Activo',
  });

  useEffect(() => {
    fetchVehiculos();
  }, []);

  const fetchVehiculos = async () => {
    try {
      const response = await axios.get(`${API}/vehiculos`);
      setVehiculos(response.data);
    } catch (error) {
      toast.error('Error al cargar vehículos');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (vehiculo = null) => {
    if (vehiculo) {
      setEditingVehiculo(vehiculo);
      setVehiculoForm({
        placa: vehiculo.placa,
        marca: vehiculo.marca,
        modelo: vehiculo.modelo,
        ano: vehiculo.ano,
        tipo_vehiculo: vehiculo.tipo_vehiculo,
        color: vehiculo.color,
        numero_vin: vehiculo.numero_vin || '',
        numero_motor: vehiculo.numero_motor || '',
        serie: vehiculo.serie || '',
        capacidad: vehiculo.capacidad || '',
        estado_actual: vehiculo.estado_actual,
      });
    } else {
      setEditingVehiculo(null);
      setVehiculoForm({
        placa: '',
        marca: '',
        modelo: '',
        ano: new Date().getFullYear(),
        tipo_vehiculo: 'Pickup',
        color: '',
        numero_vin: '',
        numero_motor: '',
        serie: '',
        capacidad: '',
        estado_actual: 'Activo',
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (!vehiculoForm.placa || !vehiculoForm.marca || !vehiculoForm.modelo) {
        toast.error('Por favor complete los campos obligatorios');
        return;
      }

      if (editingVehiculo) {
        await axios.put(`${API}/vehiculos/${editingVehiculo.id}`, vehiculoForm);
        toast.success('Vehículo actualizado exitosamente');
      } else {
        await axios.post(`${API}/vehiculos`, vehiculoForm);
        toast.success('Vehículo registrado exitosamente');
      }
      setDialogOpen(false);
      fetchVehiculos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar vehículo');
    }
  };

  const handleDelete = async (vehiculoId) => {
    if (!window.confirm('¿Está seguro de eliminar este vehículo?')) return;

    try {
      await axios.delete(`${API}/vehiculos/${vehiculoId}`);
      toast.success('Vehículo eliminado exitosamente');
      fetchVehiculos();
    } catch (error) {
      toast.error('Error al eliminar vehículo');
    }
  };

  const getEstadoBadge = (estado) => {
    const colors = {
      'Activo': 'bg-green-100 text-green-800',
      'En mantenimiento': 'bg-yellow-100 text-yellow-800',
      'Vendido': 'bg-red-100 text-red-800',
      'En Traspaso': 'bg-blue-100 text-blue-800',
    };
    return <Badge className={colors[estado] || 'bg-gray-100 text-gray-800'}>{estado}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Cargando...</p>
      </div>
    );
  }

  return (
    <MaquinariaLayout user={user} onLogout={onLogout}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-[95%] mx-auto px-4 sm:px-6 lg:px-8 py-8"
      >
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center">
              <Tractor className="w-6 h-6 mr-2" />
              Base de Datos de Vehículos
            </CardTitle>
            <Button onClick={() => handleOpenDialog()} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Vehículo
            </Button>
          </CardHeader>
          <CardContent>
            <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Placa</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Marca/Modelo</TableHead>
                <TableHead>Año</TableHead>
                <TableHead>Color</TableHead>
                <TableHead>Capacidad</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vehiculos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-gray-500">
                    No hay vehículos registrados
                  </TableCell>
                </TableRow>
              ) : (
                vehiculos.map((vehiculo) => (
                  <TableRow key={vehiculo.id}>
                    <TableCell className="font-medium">{vehiculo.placa}</TableCell>
                    <TableCell>{vehiculo.tipo_vehiculo}</TableCell>
                    <TableCell>{vehiculo.marca} {vehiculo.modelo}</TableCell>
                    <TableCell>{vehiculo.ano}</TableCell>
                    <TableCell>{vehiculo.color}</TableCell>
                    <TableCell>{vehiculo.capacidad || '-'}</TableCell>
                    <TableCell>{getEstadoBadge(vehiculo.estado_actual)}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/expediente-vehiculo/${vehiculo.id}`)}
                          title="Ver expediente"
                        >
                          <FileText className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleOpenDialog(vehiculo)}
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600"
                          onClick={() => handleDelete(vehiculo.id)}
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* DIALOG: VEHÍCULO */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingVehiculo ? 'Editar Vehículo' : 'Nuevo Vehículo'}</DialogTitle>
            <DialogDescription>
              Complete la información del vehículo
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Placa *</Label>
              <Input
                value={vehiculoForm.placa}
                onChange={(e) => setVehiculoForm({ ...vehiculoForm, placa: e.target.value.toUpperCase() })}
                placeholder="ABC-123"
              />
            </div>

            <div className="space-y-2">
              <Label>Tipo de Vehículo *</Label>
              <Select
                value={vehiculoForm.tipo_vehiculo}
                onValueChange={(value) => setVehiculoForm({ ...vehiculoForm, tipo_vehiculo: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pickup">Pickup</SelectItem>
                  <SelectItem value="Motocicleta">Motocicleta</SelectItem>
                  <SelectItem value="Camión">Camión</SelectItem>
                  <SelectItem value="Chapulín">Chapulín</SelectItem>
                  <SelectItem value="Tractor">Tractor</SelectItem>
                  <SelectItem value="Vagoneta">Vagoneta</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Marca *</Label>
              <Input
                value={vehiculoForm.marca}
                onChange={(e) => setVehiculoForm({ ...vehiculoForm, marca: e.target.value })}
                placeholder="Toyota, Ford, etc."
              />
            </div>

            <div className="space-y-2">
              <Label>Modelo *</Label>
              <Input
                value={vehiculoForm.modelo}
                onChange={(e) => setVehiculoForm({ ...vehiculoForm, modelo: e.target.value })}
                placeholder="Hilux, Ranger, etc."
              />
            </div>

            <div className="space-y-2">
              <Label>Año</Label>
              <Input
                type="number"
                value={vehiculoForm.ano}
                onChange={(e) => setVehiculoForm({ ...vehiculoForm, ano: parseInt(e.target.value) || new Date().getFullYear() })}
                min="1900"
                max={new Date().getFullYear() + 1}
              />
            </div>

            <div className="space-y-2">
              <Label>Color</Label>
              <Input
                value={vehiculoForm.color}
                onChange={(e) => setVehiculoForm({ ...vehiculoForm, color: e.target.value })}
                placeholder="Blanco, Negro, etc."
              />
            </div>

            <div className="space-y-2">
              <Label>Número VIN</Label>
              <Input
                value={vehiculoForm.numero_vin}
                onChange={(e) => setVehiculoForm({ ...vehiculoForm, numero_vin: e.target.value.toUpperCase() })}
                placeholder="Número de identificación vehicular"
              />
            </div>

            <div className="space-y-2">
              <Label>Número de Motor</Label>
              <Input
                value={vehiculoForm.numero_motor}
                onChange={(e) => setVehiculoForm({ ...vehiculoForm, numero_motor: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Serie</Label>
              <Input
                value={vehiculoForm.serie}
                onChange={(e) => setVehiculoForm({ ...vehiculoForm, serie: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Capacidad</Label>
              <Input
                value={vehiculoForm.capacidad}
                onChange={(e) => setVehiculoForm({ ...vehiculoForm, capacidad: e.target.value })}
                placeholder="3 toneladas, 5 pasajeros, etc."
              />
            </div>

            <div className="space-y-2">
              <Label>Estado Actual</Label>
              <Select
                value={vehiculoForm.estado_actual}
                onValueChange={(value) => setVehiculoForm({ ...vehiculoForm, estado_actual: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Activo">Activo</SelectItem>
                  <SelectItem value="En mantenimiento">En mantenimiento</SelectItem>
                  <SelectItem value="Vendido">Vendido</SelectItem>
                  <SelectItem value="En Traspaso">En Traspaso</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setEditingVehiculo(null);
              setVehiculoForm({
                placa: '',
                marca: '',
                modelo: '',
                ano: new Date().getFullYear(),
                tipo_vehiculo: 'Pickup',
                color: '',
                numero_vin: '',
                numero_motor: '',
                serie: '',
                capacidad: '',
                estado_actual: 'Activo',
              });
              setDialogOpen(false);
            }}>
              Cancelar
            </Button>
            <Button onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-700">
              {editingVehiculo ? 'Actualizar' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </motion.div>
    </MaquinariaLayout>
  );
}
